import React, { useState, useCallback, useContext, useEffect } from 'react';
import { Handle, Position, useReactFlow, useEdges, useNodes } from 'reactflow';
import { ExclamationTriangleIcon, Pencil1Icon } from '@radix-ui/react-icons';
import { TbArrowsJoin } from "react-icons/tb";
import { GrConfigure } from 'react-icons/gr';
import './MatcherNode.css'; // Import the CSS file
import MatcherConfigModal from './MatcherConfigModal';
import { usePipeline } from '../../../context/PipelineContext';
import { NodeMappingContext } from '../../Canvas/Canvas';
import { reconfigureNode } from '../../../api/api';
import { notifyError } from '../../../errors/notifyError';
import { OP } from '../../../api/operations';

const isUnspecifiedMatcherValue = (value, sentinelLabel) => {
  if (value === undefined || value === null) return true;
  const normalized = String(value).trim();
  return normalized === '' || normalized.toLowerCase() === sentinelLabel.toLowerCase();
};

const MatcherNode = ({ id, data, selected }) => {
  const { cons_col_name, gen_col_name, name } = data;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isConfiguring, setIsConfiguring] = useState(false);
  const reactFlowInstance = useReactFlow();
  const edges = useEdges();
  const nodes = useNodes();
  const { session, addMatcherNode, updateSelectedNode } = usePipeline();
  const { updateNodeMapping } = useContext(NodeMappingContext);
  const upstreamUnitCount = (() => {
    const consEdge = edges.find((edge) => edge.target === id && edge.targetHandle === 'cons_input');
    const sourceNode = consEdge ? nodes.find((node) => node.id === consEdge.source) : null;
    const sourceData = sourceNode?.data || {};
    const mode = sourceData.inputMode || sourceData.consumption_input_mode;
    if (mode !== 'multi_unit_slot_monthly') return 0;
    return sourceData.preview?.unitCount || sourceData.units?.length || 0;
  })();
  
  // Log the data received by the component for debugging
  useEffect(() => {
    console.log(`MatcherNode ${id} received data:`, data);
    // Log specific column name information for debugging
    console.log(`MatcherNode ${id} column name details:`, {
      cons_col_nameFromProps: cons_col_name,
      cons_col_nameFromData: data.cons_col_name,
      gen_col_nameFromProps: gen_col_name,
      gen_col_nameFromData: data.gen_col_name
    });
    
    // Log reconfiguration state
    console.log(`MatcherNode ${id} reconfiguration state:`, {
      isConfigured: data.isConfigured,
      isApiConfigured: data.isApiConfigured,
      needsReconfiguration: data.needsReconfiguration,
      needsPartialReconfiguration: data.needsPartialReconfiguration
    });
  }, [id, data, cons_col_name, gen_col_name]);

  // Check if node is configured properly.
  // Matcher must be explicitly configured via the modal; a generated node name alone
  // should not mark it as configured.
  const isConfigured = Boolean(data.isConfigured || data.isApiConfigured);

  // Check if this node has both input connections
  const hasConsInputConnection = edges.some(edge => edge.target === id && edge.targetHandle === 'cons_input');
  const hasGenInputConnection = edges.some(edge => edge.target === id && edge.targetHandle === 'gen_input');
  const hasAllInputConnections = hasConsInputConnection && hasGenInputConnection;
  const hasAtLeastOneInput = hasConsInputConnection || hasGenInputConnection;
  
  // Get the node mapping from context
  const { getNodeMapping } = useContext(NodeMappingContext);
  
  // Get the input node names if there are connections
  const getInputNodeNames = useCallback(() => {
    const consInputEdge = edges.find(edge => edge.target === id && edge.targetHandle === 'cons_input');
    const genInputEdge = edges.find(edge => edge.target === id && edge.targetHandle === 'gen_input');
    
    const result = {
      cons_input: null,
      gen_input: null
    };
    
    // Process consumption input
    if (consInputEdge) {
      const sourceNodeId = consInputEdge.source;
      const sourceNode = nodes.find(node => node.id === sourceNodeId);
      
      if (sourceNode && sourceNode.data && sourceNode.data.name) {
        result.cons_input = sourceNode.data.name;
      } else {
        const nodeMapping = getNodeMapping();
        result.cons_input = nodeMapping[sourceNodeId] || sourceNodeId;
      }
    }
    
    // Process generation input
    if (genInputEdge) {
      const sourceNodeId = genInputEdge.source;
      const sourceNode = nodes.find(node => node.id === sourceNodeId);
      
      if (sourceNode && sourceNode.data && sourceNode.data.name) {
        result.gen_input = sourceNode.data.name;
      } else {
        const nodeMapping = getNodeMapping();
        result.gen_input = nodeMapping[sourceNodeId] || sourceNodeId;
      }
    }
    
    console.log(`MatcherNode ${id} input connections:`, result);
    
    return result;
  }, [edges, id, nodes, getNodeMapping]);
  
  // Function to check if a stored input node name matches any current connection
  const hasMatchingInput = useCallback((storedNodeName, inputType) => {
    if (!storedNodeName) return true; // If no stored name, consider it matching
    
    const inputNodes = getInputNodeNames();
    if (inputType === 'cons') {
      return inputNodes.cons_input === storedNodeName;
    } else if (inputType === 'gen') {
      return inputNodes.gen_input === storedNodeName;
    }
    return false;
  }, [getInputNodeNames]);
  
  // Check if the node needs reconfiguration
  // This happens when the node is configured but one of its inputs has changed
  // Or when the node is explicitly marked as needing reconfiguration
  const needsReconfiguration = data.needsReconfiguration || data.isApiConfigured === false || 
    (isConfigured && data.consInputNodeName && data.genInputNodeName && 
      (!hasMatchingInput(data.consInputNodeName, 'cons') || !hasMatchingInput(data.genInputNodeName, 'gen')));
      
  // Check if this node needs partial reconfiguration (one input was deleted)
  // Or when the node is explicitly marked as needing partial reconfiguration
  const needsPartialReconfiguration = data.needsPartialReconfiguration || 
    (isConfigured && ((data.consInputNodeName && !hasMatchingInput(data.consInputNodeName, 'cons')) || 
                     (data.genInputNodeName && !hasMatchingInput(data.genInputNodeName, 'gen'))));

  // Get the current node position
  const getNodePosition = useCallback(() => {
    const node = nodes.find(n => n.id === id);
    return node ? node.position : { x: 0, y: 0 };
  }, [nodes, id]);
  
  // Log computed reconfiguration state for debugging
  useEffect(() => {
    console.log(`MatcherNode ${id} computed reconfiguration state:`, {
      isConfigured,
      needsReconfiguration,
      needsPartialReconfiguration,
      hasConsInputConnection,
      hasGenInputConnection,
      hasAllInputConnections,
      hasAtLeastOneInput
    });
  }, [id, isConfigured, needsReconfiguration, needsPartialReconfiguration, 
      hasConsInputConnection, hasGenInputConnection, hasAllInputConnections, hasAtLeastOneInput]);
  
  // Function to handle reconfiguration of the input nodes
  const handleReconfigureInput = async () => {
    if (!session.pipeline_name || !name) return;
    
    try {
      setIsConfiguring(true);
      
      // Get the current input node names
      const inputNodes = getInputNodeNames();
      
      // Check if we're doing a partial reconfiguration (one input was deleted)
      const isPartialReconfiguration = needsPartialReconfiguration && 
        ((inputNodes.cons_input && !inputNodes.gen_input) || (!inputNodes.cons_input && inputNodes.gen_input));
      
      console.log(`Reconfiguring node ${name} with input nodes:`, inputNodes);
      console.log(`Partial reconfiguration: ${isPartialReconfiguration}`);
      
      // Update the UI first to provide immediate feedback
      const updateNodeUI = (consInputName, genInputName) => {
        // Update the node data
        reactFlowInstance.setNodes((nodes) =>
          nodes.map((node) => {
            if (node.id === id) {
              return {
                ...node,
                data: {
                  ...node.data,
                  isConfigured: true,
                  needsReconfiguration: false,
                  needsPartialReconfiguration: false,
                  consInputNodeName: consInputName,
                  genInputNodeName: genInputName,
                  isApiConfigured: true
                }
              };
            }
            return node;
          })
        );
        
        // Update the session state
        const position = getNodePosition();
        addMatcherNode(
          name,
          cons_col_name,
          gen_col_name,
          consInputName,
          genInputName,
          { x: position.x, y: position.y }
        );
      };
      
      if (isPartialReconfiguration) {
        // For partial reconfiguration, we only update the connected input
        if (inputNodes.cons_input) {
          try {
            // Only consumption input is connected, update it
            await reconfigureNode(
              session.pipeline_name,
              name,
              inputNodes.cons_input,
              0  // inputIndex = 0 for the consumption input
            );
            console.log(`Node ${name} consumption input reconfigured to ${inputNodes.cons_input}`);
          } catch (error) {
            console.error('API error reconfiguring consumption input:', error);
            // Continue with UI update even if API call fails
            console.log('Continuing with UI update despite API error');
          }
          
          // Keep the previous value for generation input if it exists
          const genInputName = data.genInputNodeName || null;
          
          // Update the UI
          updateNodeUI(inputNodes.cons_input, genInputName);
          
        } else if (inputNodes.gen_input) {
          try {
            // Only generation input is connected, update it
            await reconfigureNode(
              session.pipeline_name,
              name,
              inputNodes.gen_input,
              1  // inputIndex = 1 for the generation input
            );
            console.log(`Node ${name} generation input reconfigured to ${inputNodes.gen_input}`);
          } catch (error) {
            console.error('API error reconfiguring generation input:', error);
            // Continue with UI update even if API call fails
            console.log('Continuing with UI update despite API error');
          }
          
          // Keep the previous value for consumption input if it exists
          const consInputName = data.consInputNodeName || null;
          
          // Update the UI
          updateNodeUI(consInputName, inputNodes.gen_input);
        }
      } else {
        // Regular reconfiguration with both inputs
        if (!inputNodes.cons_input || !inputNodes.gen_input) {
          throw new Error('Both input nodes must be connected for full reconfiguration');
        }
        
        let apiError = null;
        
        try {
          // First, reconfigure with the consumption input
          await reconfigureNode(
            session.pipeline_name,
            name,
            inputNodes.cons_input,
            0  // inputIndex = 0 for the consumption input
          );
          console.log(`Node ${name} consumption input reconfigured to ${inputNodes.cons_input}`);
        } catch (error) {
          console.error('API error reconfiguring consumption input:', error);
          apiError = error;
        }
        
        try {
          // Then, reconfigure with the generation input
          await reconfigureNode(
            session.pipeline_name,
            name,
            inputNodes.gen_input,
            1  // inputIndex = 1 for the generation input
          );
          console.log(`Node ${name} generation input reconfigured to ${inputNodes.gen_input}`);
        } catch (error) {
          console.error('API error reconfiguring generation input:', error);
          if (!apiError) apiError = error;
        }
        
        // Update the UI even if API calls failed
        updateNodeUI(inputNodes.cons_input, inputNodes.gen_input);
        
        // If there was an API error, log it but don't alert the user since the UI is updated
        if (apiError) {
          console.warn('API errors occurred during reconfiguration, but UI was updated:', apiError);
        }
      }
      
      setIsConfiguring(false);
    } catch (error) {
      console.error('Error in reconfiguration process:', error);
      // Update the UI anyway to prevent the node from being stuck in reconfiguration state
      reactFlowInstance.setNodes((nodes) =>
        nodes.map((node) => {
          if (node.id === id) {
            return {
              ...node,
              data: {
                ...node.data,
                needsReconfiguration: false,
                needsPartialReconfiguration: false,
                isApiConfigured: true
              }
            };
          }
          return node;
        })
      );
      setIsConfiguring(false);
    }
  };

  const openConfigModal = useCallback(() => {
    // For initial configuration, we need both inputs
    if (!isConfigured && !hasAllInputConnections) {
      // Show an alert if there's no input connection
      notifyError(new Error("Please connect both consumption and generation inputs to this matcher node before configuring it."), {
        toastId: 'matcher.connectBothInputs',
      });
      return;
    }
    
    // For reconfiguration, we need at least one input
    if ((needsReconfiguration || needsPartialReconfiguration || data.isApiConfigured === false) && !hasAtLeastOneInput) {
      notifyError(new Error("Please connect at least one input to this matcher node before reconfiguring it."), {
        toastId: 'matcher.connectOneInput',
      });
      return;
    }
    
    // If the node is already configured and the user is trying to edit it (not reconfigure)
    if (isConfigured && !needsReconfiguration && !needsPartialReconfiguration && data.isApiConfigured !== false) {
      // Open the modal in edit mode
      console.log("Opening modal for editing existing matcher node");
      setIsModalOpen(true);
      return;
    }
    
    setIsModalOpen(true);
  }, [hasAllInputConnections, hasAtLeastOneInput, isConfigured, needsReconfiguration, needsPartialReconfiguration, data.isApiConfigured]);

  const closeConfigModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const handleConfigured = useCallback(async (configData) => {
    // Start the configuration process
    setIsConfiguring(true);
    
    try {
      const { name, cons_col_name, gen_col_name } = configData;
      const inputNodes = getInputNodeNames();
      const position = getNodePosition();
      
      // Log the configuration data for debugging
      console.log(`Configuring matcher node ${id} -> ${name}:`, {
        configData,
        inputNodes,
        position,
        edges: edges.filter(edge => edge.target === id || edge.source === id)
      });
      
      // Add the matcher node to the session state
      if (inputNodes.cons_input && inputNodes.gen_input) {
        // First update the node ID to name mapping before adding to session
        // This ensures the correct name is used in the pipeline state
        updateNodeMapping(id, name);
        console.log(`Updated node mapping before adding to session: ${id} -> ${name}`);
        
        console.log(`Adding matcher node to session:`, {
          name,
          cons_col_name,
          gen_col_name,
          consNodeName: inputNodes.cons_input,
          genNodeName: inputNodes.gen_input,
          position
        });
        
        // Now add the node to the session with the correct name
        addMatcherNode(
          name,
          cons_col_name,
          gen_col_name,
          inputNodes.cons_input,
          inputNodes.gen_input,
          { x: position.x, y: position.y }
        );
        
        // Update the node data with the new configuration
        reactFlowInstance.setNodes((nodes) =>
          nodes.map((node) => {
            if (node.id === id) {
              return {
                ...node,
                data: {
                  ...node.data,
                  name,
                  cons_col_name,
                  gen_col_name,
                  consInputNodeName: inputNodes.cons_input,
                  genInputNodeName: inputNodes.gen_input,
                  isConfigured: true,
                  needsReconfiguration: false,
                  needsPartialReconfiguration: false,
                  isApiConfigured: true
                }
              };
            }
            return node;
          })
        );
        
        // If the node was previously selected, update the selection with the new name
        if (session.selected_node === id) {
          console.log(`Node was selected, updating selection from ${id} to ${name}`);
          updateSelectedNode(name);
        }
      } else {
        // Just update the UI if we're not connected to a backend
        // Still update the mapping first
        updateNodeMapping(id, name);
        console.log(`Updated node mapping in local mode: ${id} -> ${name}`);
        
        reactFlowInstance.setNodes((nodes) =>
          nodes.map((node) => {
            if (node.id === id) {
              return {
                ...node,
                data: {
                  ...node.data,
                  name,
                  cons_col_name,
                  gen_col_name,
                  consInputNodeName: inputNodes.cons_input || null,
                  genInputNodeName: inputNodes.gen_input || null,
                  isConfigured: true,
                  needsReconfiguration: false,
                  needsPartialReconfiguration: false
                }
              };
            }
            return node;
          })
        );
        
        // Update selection if needed
        if (session.selected_node === id) {
          updateSelectedNode(name);
        }
      }
    } catch (error) {
      console.error('Error configuring matcher node:', error);
      notifyError(error, {
        operation: (needsReconfiguration || needsPartialReconfiguration) ? OP.node.reconfigure : OP.node.editMatcher,
      });
    } finally {
      setIsConfiguring(false);
      closeConfigModal();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, reactFlowInstance, getInputNodeNames, getNodePosition, closeConfigModal, updateNodeMapping, addMatcherNode, session, updateSelectedNode, edges]);

  return (
    <>
      <div
        className={`matcher-node ${selected ? 'matcher-selected' : 
                               (needsReconfiguration || needsPartialReconfiguration || data.isApiConfigured === false) ? 'matcher-needs-reconfiguration' : 
                               isConfigured ? 'matcher-configured' : 'matcher-not-configured'}`}
      >
        <div className={`matcher-node-header ${(needsReconfiguration || needsPartialReconfiguration || data.isApiConfigured === false) ? 'matcher-needs-reconfiguration' : 
                                           isConfigured ? 'matcher-configured' : 'matcher-not-configured'}`}>
          <div className="matcher-node-header-title">
            <div className="matcher-node-type">Matcher</div>
            {name && (isConfigured || needsReconfiguration || needsPartialReconfiguration) && 
              <div className="matcher-node-name-display">{name}</div>}
          </div>
          <div className="matcher-node-header-icons">
            {/* Show different buttons based on node state */}
            {needsReconfiguration || needsPartialReconfiguration || data.isApiConfigured === false ? (
              <button 
                onClick={handleReconfigureInput}
                className="matcher-config-button matcher-reconfigure-button"
                title="Reconfigure input nodes"
                disabled={(needsReconfiguration && !hasAllInputConnections && !needsPartialReconfiguration) || 
                         (needsPartialReconfiguration && !hasAtLeastOneInput) || 
                         isConfiguring}
              >
                <GrConfigure className="matcher-icon" />
              </button>
            ) : (
              <button 
                onClick={openConfigModal}
                className="matcher-config-button"
                title={isConfigured ? "Edit Configuration" : hasAllInputConnections ? "Configure Matcher" : "Connect both inputs first"}
                disabled={!hasAllInputConnections || isConfiguring}
              >
                {isConfigured ? (
                  <Pencil1Icon className="matcher-icon" />
                ) : (
                  <ExclamationTriangleIcon className="matcher-icon" />
                )}
              </button>
            )}
            <TbArrowsJoin className="matcher-icon" />
          </div>
        </div>
        <div className="matcher-node-content">
          <div className="matcher-text-xs">
            {(!hasConsInputConnection || !hasGenInputConnection) && (
              <div className="matcher-text-warning">
                Connect both inputs to configure
              </div>
            )}
            {hasAllInputConnections && !isConfigured && (
              <div className="matcher-text-warning">
                Parameters not configured
              </div>
            )}
            {isConfiguring && (
              <div className="matcher-text-info">
                Configuring...
              </div>
            )}
            {isConfigured && (
              <>
                <div>Generation Column: {
                  !isUnspecifiedMatcherValue(gen_col_name, 'Generation column not specified') ? 
                    gen_col_name : 
                    (!isUnspecifiedMatcherValue(data.gen_col_name, 'Generation column not specified') ? 
                      data.gen_col_name : 
                      'Auto')
                }</div>
                <div>Consumption Column: {
                  !isUnspecifiedMatcherValue(cons_col_name, 'Consumption column not specified') ? 
                    cons_col_name : 
                    (!isUnspecifiedMatcherValue(data.cons_col_name, 'Consumption column not specified') ? 
                      data.cons_col_name : 
                      'Auto')
                }</div>
                {!!upstreamUnitCount && <div>Units: {upstreamUnitCount}</div>}
              </>
            )}
          </div>
          <div className="matcher-flex-container">
            <Handle
              type="target"
              position={Position.Left}
              id="gen_input"
              className={`matcher-connection-point matcher-gen-input ${isConfigured ? 'matcher-configured' : 'matcher-not-configured'}`}
              data-maxconnections="1" // Custom attribute to indicate this handle should only have one connection
              style={{ top: '35%', left: '-4px' }} // Position slightly outward
            />
            <Handle
              type="target"
              position={Position.Left}
              id="cons_input"
              className={`matcher-connection-point matcher-cons-input ${isConfigured ? 'matcher-configured' : 'matcher-not-configured'}`}
              data-maxconnections="1" // Custom attribute to indicate this handle should only have one connection
              style={{ top: '65%', left: '-4px' }} // Position slightly outward
            />
            <Handle
              type="source"
              position={Position.Right}
              id="output"
              className={`matcher-connection-point ${isConfigured ? 'matcher-configured-output' : 'matcher-not-configured'}`}
              isConnectable={isConfigured} // Only allow connections if the node is configured
              data-maxconnections="1" // Custom attribute to indicate this handle should only have one connection
              style={{ right: '-4px' }} // Position slightly outward
            />
          </div>
        </div>
      </div>

      <MatcherConfigModal
        isOpen={isModalOpen}
        onClose={closeConfigModal}
        onConfigured={handleConfigured}
        isReconfiguration={needsReconfiguration || needsPartialReconfiguration || data.isApiConfigured === false}
        initialData={{ 
          name, 
          cons_col_name,
          gen_col_name,
          inputNodes: getInputNodeNames(),
          position: getNodePosition(),
          // Use backend-configured flag to decide edit vs append
          isConfigured: data.isApiConfigured === true
        }}
      />
    </>
  );
};

export default MatcherNode;
